import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

/* ─────────────────────────────────────────────
   Shared option lists
───────────────────────────────────────────── */
const PACKAGE_UNIT_OPTIONS = [
  { value: "", label: "-Select-" },
  { value: "BDL", label: "BUNDLE (BDL)" },
  { value: "CAS", label: "CASES (CAS)" },
  { value: "SHT", label: "SHEETS (SHT)" },
  { value: "CHT", label: "CHEST (CHT)" },
  { value: "CLS", label: "COILS (CLS)" },
  { value: "COL", label: "COLLIES (COL)" },
  { value: "CON", label: "CONTAINER (CON)" },
  { value: "CRT", label: "CRATES (CRT)" },
  { value: "CSK", label: "WOODEN CASKS (CSK)" },
  { value: "CTN", label: "CARTONS (CTN)" },
  { value: "CYL", label: "CYLINDER (CYL)" },
  { value: "DRM", label: "DRUMS (DRM)" },
  { value: "ENV", label: "STEEL ENVELOPES (ENV)" },
  { value: "FLK", label: "FLASK (FLK)" },
  { value: "FUT", label: "FUTS (FUT)" },
  { value: "HBK", label: "HABBUCK (HBK)" },
  { value: "JBL", label: "JUMBLE BALE (JBL)" },
  { value: "JTA", label: "JOTTA (JTA)" },
  { value: "KEG", label: "KEGGS (KEG)" },
  { value: "LFT", label: "LIFT (LFT)" },
  { value: "LOG", label: "LOGS (LOG)" },
  { value: "NGT", label: "INGOT (NGT)" },
  { value: "PAL", label: "PALLS (PAL)" },
  { value: "PKG", label: "PACKAGES (PKG)" },
  { value: "PLT", label: "PALLETS (PLT)" },
  { value: "QDS", label: "QUADS (QDS)" },
  { value: "REL", label: "REELS (REL)" },
  { value: "RLS", label: "ROOLS (RLS)" },
  { value: "SKD", label: "SKID & SKIDDED PKGS (SKD)" },
  { value: "SLB", label: "SLABS (SLB)" },
  { value: "TBL", label: "TABLE (TBL)" },
  { value: "TIN", label: "TINS (TIN)" },
  { value: "TRK", label: "TRUNK (TRK)" },
  { value: "UNT", label: "UNITS (UNT)" },
  { value: "PCS", label: "PIECES (PCS)" },
  { value: "BAL", label: "BALES" },
];

const emptyPackageLine = () => ({
  _key: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  no_of_packages: "",
  package_unit: "",
});

/** Human-readable "20 Bales, 15 Bags" summary from a packages array. */
const summarizePackages = (packages) => {
  if (!Array.isArray(packages) || packages.length === 0) return "—";
  return packages
    .filter((p) => p.no_of_packages || p.package_unit)
    .map((p) => `${p.no_of_packages || 0} ${p.package_unit || ""}`.trim())
    .join(", ") || "—";
};

/** Guards against "Invalid Date" strings — returns "—" for empty/unparsable values. */
const fmtDate = (d) => {
  if (!d) return "—";
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const CONTAINER_SIZE_OPTIONS = [
  { value: "", label: "-Select-" },
  { value: "20FT_GENERAL", label: "20FT · General" },
  { value: "20FT_HIGH_CUBE", label: "20FT · High Cube" },
  { value: "20FT_FLAT_TRACK", label: "20FT · Flat Rack" },
  { value: "20FT_REEFER", label: "20FT · Reefer" },
  { value: "20FT_OPEN_TOP", label: "20FT · Open Top" },
  { value: "20FT_TANK", label: "20FT · Tank" },
  { value: "20FT_HARD_TOP", label: "20FT · Hard Top" },
  { value: "40FT_GENERAL", label: "40FT · General" },
  { value: "40FT_HIGH_CUBE", label: "40FT · High Cube" },
  { value: "40FT_FLAT_TRACK", label: "40FT · Flat Rack" },
  { value: "40FT_REEFER", label: "40FT · Reefer" },
  { value: "40FT_OPEN_TOP", label: "40FT · Open Top" },
  { value: "40FT_TANK", label: "40FT · Tank" },
  { value: "40FT_HARD_TOP", label: "40FT · Hard Top" },
  { value: "45FT_STANDARD", label: "45FT · Standard" },
];
const CONTAINER_SIZE_LABELS = CONTAINER_SIZE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const containerSizeLabel = (v) => (v ? CONTAINER_SIZE_LABELS[v] || v : "—");

/* ─────────────────────────────────────────────
   ContainerImageModal – gallery + upload
   (unchanged from your original file)
───────────────────────────────────────────── */
const ContainerImageModal = ({ show, container, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);   // { src, name }
  const [lightbox, setLightbox] = useState(null);   // image_path string
  const fileInputRef = useRef();

  const fetchImages = useCallback(async () => {
    if (!container) return;
    setLoading(true);
    try {
      const res = await post("/all_jobs/containers/images", {
        bl_number: container.bl_number,
        container_no: container.container_no,
      });
      setImages(res.status === 200 ? res.data : []);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [container]);

  useEffect(() => {
    if (show && container) {
      fetchImages();
      setCaption("");
      setPreview(null);
    }
  }, [show, container, fetchImages]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreview({ src: ev.target.result, name: file.name });
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      Swal.fire({ icon: "warning", title: "No file selected", timer: 1800, showConfirmButton: false });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("bl_number", container.bl_number);
      formData.append("container_no", container.container_no);
      formData.append("caption", caption);

      const data = await post("all_jobs/containers/upload_image", formData);

      if (data.status === 200) {
        Swal.fire({ icon: "success", title: "Uploaded!", timer: 1500, showConfirmButton: false });
        setPreview(null);
        setCaption("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchImages();
      } else {
        Swal.fire({ icon: "error", title: "Upload Failed", text: data.message });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (image_id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete image?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await post("/all_jobs/containers/delete_image", { image_id });
      if (res.status === 200) {
        Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
        fetchImages();
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1056 }} tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content" style={{ borderRadius: 12, overflow: "hidden" }}>
            <div className="modal-header" style={{ background: "rgba(10,24,50,0.97)", color: "#fff" }}>
              <div>
                <h5 className="modal-title mb-0">
                  <i className="ri-image-2-line me-2" />
                  Container Images
                </h5>
                {container && (
                  <small style={{ opacity: 0.85 }}>
                    {container.container_no} &nbsp;·&nbsp; BL: {container.bl_number}
                  </small>
                )}
              </div>
              <button type="button" className="btn-close btn-close-white" style={{ color: "#fff" }} onClick={onClose} />
            </div>

            <div className="modal-body" style={{ background: "#f8f9fa" }}>
              <div className="card mb-4 shadow-sm">
                <div className="card-header py-2">
                  <strong className="text-muted" style={{ fontSize: 13 }}>
                    <i className="ri-upload-cloud-2-line me-1" />
                    Upload New Image
                  </strong>
                </div>
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Choose Image</label>
                      <input ref={fileInputRef} type="file" className="form-control form-control-sm" accept="image/*" onChange={handleFileChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                        Caption <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input type="text" className="form-control form-control-sm" placeholder="e.g. Front view, Damage photo…" value={caption} onChange={(e) => setCaption(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <button className="btn btn-primary btn-sm w-100" onClick={handleUpload} disabled={uploading || !preview}>
                        {uploading ? (<><span className="spinner-border spinner-border-sm me-1" />Uploading…</>) : (<><i className="ri-upload-line me-1" />Upload</>)}
                      </button>
                    </div>
                    {preview && (
                      <div className="col-md-2 text-center">
                        <img src={preview.src} alt="preview" style={{ height: 60, width: 80, objectFit: "cover", borderRadius: 6, border: "2px solid #0d6efd" }} />
                        <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{preview.name}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-header py-2 d-flex justify-content-between align-items-center">
                  <strong className="text-muted" style={{ fontSize: 13 }}>
                    <i className="ri-gallery-line me-1" />
                    Uploaded Images
                  </strong>
                  <span className="badge bg-secondary">{images.length}</span>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" />
                      <div className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading images…</div>
                    </div>
                  ) : images.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ri-image-line" style={{ fontSize: 48, opacity: 0.3 }} />
                      <div className="mt-2" style={{ fontSize: 13 }}>No images uploaded yet.</div>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {images.map((img) => (
                        <div className="col-6 col-md-3 col-lg-2" key={img.image_id}>
                          <div className="card h-100 shadow-sm" style={{ border: "1px solid #dee2e6", borderRadius: 10, overflow: "hidden", transition: "transform 0.15s", cursor: "pointer" }}
                            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-3px)")}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                          >
                            <div style={{ position: "relative", overflow: "hidden", height: 120 }} onClick={() => setLightbox(img.image_path)}>
                              <img
                                src={img.image_path}
                                alt={img.image_name}
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                              />
                              <div style={{ display: "none", height: "100%", alignItems: "center", justifyContent: "center", background: "#e9ecef", fontSize: 30, color: "#adb5bd" }}>
                                <i className="ri-image-line" />
                              </div>
                              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }} className="img-overlay">
                                <i className="ri-zoom-in-line text-white" style={{ fontSize: 24 }} />
                              </div>
                            </div>
                            <div className="card-body p-2">
                              {img.caption && (
                                <div className="fw-semibold text-truncate" style={{ fontSize: 11 }} title={img.caption}>{img.caption}</div>
                              )}
                              <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                                {fmtDate(img.date_added)}
                              </div>
                              <div className="d-flex gap-1 mt-2">
                                <a href={img.image_path} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm flex-fill" style={{ fontSize: 10, padding: "2px 4px" }} title="View full image" onClick={(e) => e.stopPropagation()}>
                                  <i className="ri-external-link-line" />
                                </a>
                                <button className="btn btn-danger btn-sm flex-fill" style={{ fontSize: 10, padding: "2px 4px", background: "#dc3545" }} title="Delete" onClick={(e) => { e.stopPropagation(); handleDelete(img.image_id); }}>
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <small className="text-muted me-auto" style={{ fontSize: 11 }}>Accepted formats: JPG, PNG, WEBP</small>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setLightbox(null)}>
          <button style={{ position: "absolute", top: 18, right: 24, background: "none", border: "none", color: "#fff", fontSize: 32, cursor: "pointer" }} onClick={() => setLightbox(null)}>
            <i className="ri-close-line" />
          </button>
          <img src={lightbox} alt="Full view" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 10, boxShadow: "0 10px 60px rgba(0,0,0,0.6)" }} onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <style>{`.card:hover .img-overlay { opacity: 1 !important; }`}</style>
    </>
  );
};

/* ─────────────────────────────────────────────
   PackageLinesEditor – repeatable "qty + unit" rows
   Lets a single container carry MORE THAN ONE
   package type (e.g. 20 Bales + 15 Bags).
───────────────────────────────────────────── */
const PackageLinesEditor = ({ lines, onChange }) => {
  const updateLine = (key, field, value) => {
    onChange(lines.map((l) => (l._key === key ? { ...l, [field]: value } : l)));
  };

  const addLine = () => onChange([...lines, emptyPackageLine()]);

  const removeLine = (key) => {
    if (lines.length === 1) {
      // keep at least one row, just clear it
      onChange([emptyPackageLine()]);
      return;
    }
    onChange(lines.filter((l) => l._key !== key));
  };

  const total = lines.reduce((sum, l) => sum + (parseFloat(l.no_of_packages) || 0), 0);

  return (
    <div className="col-12">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-semibold mb-0" style={{ fontSize: 13 }}>
          Packages <span className="text-muted fw-normal">(add a line per package type)</span>
        </label>
        <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
          Total: {total || 0}
        </span>
      </div>

      <div className="d-flex flex-column gap-2">
        {lines.map((line, idx) => (
          <div className="row g-2 align-items-center" key={line._key}>
            <div className="col-4 col-md-3">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="No. of Packages"
                value={line.no_of_packages}
                onChange={(e) => updateLine(line._key, "no_of_packages", e.target.value)}
              />
            </div>
            <div className="col-5 col-md-4">
              <select
                className="form-select form-select-sm"
                value={line.package_unit}
                onChange={(e) => updateLine(line._key, "package_unit", e.target.value)}
              >
                {PACKAGE_UNIT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                title="Remove line"
                onClick={() => removeLine(line._key)}
              >
                <i className="ri-close-line" />
              </button>
            </div>
            {idx === lines.length - 1 && (
              <div className="col-auto">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLine} title="Add another package type">
                  <i className="ri-add-line me-1" />
                  Add
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   ContainerFormModal – add/edit container.
   Renders the generic formFields plus the
   PackageLinesEditor for multi-package-type support.
───────────────────────────────────────────── */
const ContainerFormModal = ({ show, title, formFields, formData, packageLines, onChange, onPackageLinesChange, onClose, onSubmit, submitText = "Save" }) => {
  if (!show) return null;

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      className: field.type === "select" ? "form-select form-select-sm" : "form-control form-control-sm",
      value: formData[field.name] ?? "",
      onChange,
      disabled: field.disabled,
    };

    let control;
    if (field.type === "select") {
      control = (
        <select {...commonProps}>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    } else if (field.type === "textarea") {
      control = <textarea {...commonProps} rows={3} />;
    } else {
      control = <input type={field.type || "text"} {...commonProps} />;
    }

    return (
      <div className={`col-md-${field.col || 6}`} key={field.name}>
        <label className="form-label fw-semibold" style={{ fontSize: 13 }} htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </label>
        {control}
      </div>
    );
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1056 }} tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content" style={{ borderRadius: 12, overflow: "hidden" }}>
            <div className="modal-header" style={{ background: "rgba(10,24,50,0.97)", color: "#fff" }}>
              <h5 className="modal-title mb-0">{title}</h5>
              <button type="button" className="btn-close btn-close-white" style={{ color: "#fff" }} onClick={onClose} />
            </div>

            <div className="modal-body" style={{ background: "#f8f9fa" }}>
              <div className="row g-3">
                {formFields.map(renderField)}
                <PackageLinesEditor lines={packageLines} onChange={onPackageLinesChange} />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={onSubmit}>{submitText}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   BLContainerDetailModal
   Opened by clicking a row in the BL-grouped table.
   Shows every container under that bl_no as a TABLE
   (one row per container), with an Images action
   wired to the existing ContainerImageModal.
───────────────────────────────────────────── */
const BLContainerDetailModal = ({ show, blNo, rows, onClose, onOpenImages }) => {
  if (!show) return null;

  const totalImages = rows.reduce((sum, r) => sum + (parseInt(r.image_count) || 0), 0);

  // Only show a column if at least one row actually has data for it —
  // this is what was making the table feel noisy/empty before.
  const hasType = rows.some((r) => r.container_type);
  const hasTare = rows.some((r) => r.tare_weight);
  const hasFreeDays = rows.some((r) => r.free_days);
  const hasCargo = rows.some((r) => r.cargo_description);
  const hasRemarks = rows.some((r) => r.remarks);

  const th = (label, extra = {}) => (
    <th
      style={{
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        background: "#eef1f6",
        color: "#3a4459",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontWeight: 700,
        padding: "10px 12px",
        borderBottom: "2px solid #dfe3ea",
        zIndex: 1,
        ...extra,
      }}
    >
      {label}
    </th>
  );

  const td = (content, extra = {}) => (
    <td style={{ padding: "9px 12px", verticalAlign: "middle", ...extra }}>{content}</td>
  );

  const truncate = (text, max = 28) => {
    if (!text) return "—";
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1056 }} tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content" style={{ borderRadius: 12, overflow: "hidden", border: "none", boxShadow: "0 20px 60px rgba(10,24,50,0.25)" }}>

            <div className="modal-header" style={{ background: "rgba(10,24,50,0.97)", color: "#fff", padding: "16px 20px" }}>
              <div>
                <h5 className="modal-title mb-0" style={{ fontSize: 17, fontWeight: 600 }}>
                  <i className="ri-file-list-3-line me-2" />
                  BL No. {blNo}
                </h5>
                <small style={{ opacity: 0.75, fontSize: 12.5 }}>
                  {rows.length} container{rows.length === 1 ? "" : "s"} &nbsp;·&nbsp; {totalImages} image{totalImages === 1 ? "" : "s"}
                </small>
              </div>
              <button type="button" className="btn-close btn-close-white" style={{ color: "#fff" }} onClick={onClose} />
            </div>

            <div className="modal-body p-0" style={{ background: "#fff" }}>
              {rows.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="ri-inbox-line" style={{ fontSize: 40, opacity: 0.3 }} />
                  <div className="mt-2" style={{ fontSize: 13 }}>No containers found for this BL number.</div>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "65vh" }}>
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: 13, borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {th("Container No.", { position: "sticky", left: 0, zIndex: 2, boxShadow: "1px 0 0 #dfe3ea" })}
                        {th("Seal No.")}
                        {th("Size")}
                        {hasType && th("Type")}
                        {hasTare && th("Tare Wt", { textAlign: "right" })}
                        {th("Gross Wt", { textAlign: "right" })}
                        {th("Packages")}
                        {th("Arrival")}
                        {th("Shifting")}
                        {th("CFS Storage")}
                        {th("Empty Return")}
                        {hasFreeDays && th("Free Days", { textAlign: "right" })}
                        {hasCargo && th("Cargo")}
                        {hasRemarks && th("Remarks")}
                        {th("Images", { textAlign: "center" })}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c, idx) => (
                        <tr key={c.container_id ?? c.container_no} style={{ background: idx % 2 ? "#fafbfc" : "#fff" }}>
                          {td(
                            <>
                              <i className="ri-archive-2-line me-1 text-primary" />
                              <span className="fw-semibold">{c.container_no || "—"}</span>
                            </>,
                            { position: "sticky", left: 0, background: idx % 2 ? "#fafbfc" : "#fff", boxShadow: "1px 0 0 #eef0f3" }
                          )}
                          {td(c.seal_no || "—")}
                          {td(
                            c.container_size ? (
                              <span className="badge" style={{ background: "#eef2ff", color: "#3949ab", fontWeight: 500, fontSize: 11.5 }}>
                                {containerSizeLabel(c.container_size)}
                              </span>
                            ) : "—"
                          )}
                          {hasType && td(c.container_type || "—")}
                          {hasTare && td(c.tare_weight ? `${c.tare_weight} ${c.weight_unit || ""}` : "—", { textAlign: "right" })}
                          {td(c.gross_weight ? `${c.gross_weight} ${c.weight_unit || ""}` : "—", { textAlign: "right" })}
                          {td(<span title={summarizePackages(c.packages)}>{summarizePackages(c.packages)}</span>)}
                          {td(fmtDate(c.arrival_date))}
                          {td(fmtDate(c.shifting_date))}
                          {td(fmtDate(c.storage_date))}
                          {td(fmtDate(c.yard_date))}
                          {hasFreeDays && td(c.free_days ?? "—", { textAlign: "right" })}
                          {hasCargo && td(<span title={c.cargo_description}>{truncate(c.cargo_description)}</span>)}
                          {hasRemarks && td(<span className="fst-italic text-muted" title={c.remarks}>{truncate(c.remarks)}</span>)}
                          {td(
                            <span
                              className={`badge ${(c.image_count || 0) > 0 ? "bg-success" : "bg-secondary"}`}
                              style={{ cursor: "pointer" }}
                              onClick={() => onOpenImages({ container_no: c.container_no, bl_number: blNo })}
                              title="View / upload images"
                            >
                              <i className="ri-image-line me-1" />
                              {c.image_count || 0}
                            </span>,
                            { textAlign: "center" }
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main Containers component
───────────────────────────────────────────── */
const Containers = () => {
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);

  const formFields = useMemo(() => [
    { name: "job_id_fk", label: "BL No.", type: "select", required: true, col: 6, options: jobOptions },
    { name: "container_no", label: "Container No", type: "text", col: 6 },
    { name: "seal_no", label: "Seal No.", type: "text", col: 6 },
    {
      name: "container_type", label: "Container Type", type: "select", col: 6, disabled: true,
      options: [
        { value: "", label: "-Select-" },
        { value: "FCL", label: "FCL" },
        { value: "LCL", label: "LCL" },
      ]
    },
    { name: "cargo_description", label: "Cargo Description", type: "select", col: 12, disabled: true, options: commodityOptions },
    { name: "container_size", label: "Container Size", type: "select", col: 3, options: CONTAINER_SIZE_OPTIONS },
    { name: "weight_unit", label: "Weight Unit", type: "select", col: 3, options: [{ value: "mt", label: "MT" }, { value: "kg", label: "KG" }] },
    { name: "tare_weight", label: "Tare Weight (MT)", type: "number", col: 3 },
    { name: "gross_weight", label: "Gross Weight (MT)", type: "number", col: 3 },
    // NOTE: `no_of_packages` / `package_unit` are no longer single
    // fields here — a container can have MULTIPLE package types,
    // captured below via the repeatable PackageLinesEditor.
    { name: "free_days", label: "Free Days", type: "number", col: 3 },
    { name: "arrival_date", label: "Arrival Date", type: "date", col: 3 },
    { name: "shifting_date", label: "Shifting Date", type: "date", col: 3 },
    { name: "storage_date", label: "CFS Storage Date", type: "date", col: 3 },
    { name: "yard_date", label: "Empty Return Date", type: "date", col: 4 },
    { name: "remarks", label: "Remarks", type: "textarea", col: 12 },
  ], [jobOptions, commodityOptions]);

  const initialFormState = useMemo(() =>
    formFields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), { container_id: 0 }),
    [formFields]
  );

  const [formData, setFormData] = useState(initialFormState);
  const [packageLines, setPackageLines] = useState([emptyPackageLine()]);
  const [containerList, setContainersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  /* image modal state */
  const [imageModal, setImageModal] = useState(false);
  const [imageContainer, setImageContainer] = useState(null); // { container_no, bl_number }

  /* BL detail modal state */
  const [blDetailModal, setBlDetailModal] = useState(false);
  const [activeBl, setActiveBl] = useState(null);

  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await post("/all_jobs/containers/list");
      if (response.status === 200) setContainersList(response.data);
      else showAlert("error", response.status, response.message);
    } catch (err) { showAlert("error", "Unexpected Error", err.message); }
  }, [showAlert]);

  const fetchJobData = useCallback(async () => {
    try {
      const response = await post("/all_jobs/job/list");
      if (response.status === 200) {
        setJobOptions([
          { value: "", label: "Select Job" },
          ...response.data.map((item) => ({
            value: item.job_id,
            label: item.bl_number,
            cargo_desc: item.cargo_desc,
            container_type: item.container_type,
          })),
        ]);
      }
    } catch (err) { console.log(err.message); }
  }, []);

  const fetchCommodityDate = useCallback(async () => {
    try {
      const response = await post("/masters/commodity/list");
      if (response.status === 200) {
        setCommodityOptions([
          { value: "", label: "Select Commodity" },
          ...response.data.map((item) => ({ value: item.commodity_id, label: item.full_name })),
        ]);
      }
    } catch (err) { console.log(err.message); }
  }, []);

  useEffect(() => {
    fetchData();
    fetchJobData();
    fetchCommodityDate();
  }, [fetchData, fetchJobData, fetchCommodityDate]);

  /* group the flat container list by bl_no */
  const blGroups = useMemo(() => {
    const groups = {};
    containerList.forEach((row) => {
      const key = row.bl_no || "(No BL No.)";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    return groups;
  }, [containerList]);

  const blTableData = useMemo(
    () =>
      Object.keys(blGroups)
        .sort((a, b) => a.localeCompare(b))
        .map((bl_no, idx) => {
          const rows = blGroups[bl_no];
          const firstRow = rows[0];

          return {
            s_no: idx + 1,
            bl_no,
            job_no: firstRow?.job_no || "",
            consignees_name: firstRow?.consignees_name || "",
            container_count: rows.length,
            image_count: rows.reduce(
              (sum, r) => sum + (parseInt(r.image_count, 10) || 0),
              0
            ),
            _rows: rows,
          };
        }),
    [blGroups]
  );

  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });

    // Prefer the fetched `packages` breakdown; fall back to the
    // legacy single no_of_packages/package_unit if that's all we have.
    if (Array.isArray(row.packages) && row.packages.length > 0) {
      setPackageLines(
        row.packages.map((p) => ({
          _key: `pl_${p.package_id ?? Math.random()}`,
          no_of_packages: p.no_of_packages ?? "",
          package_unit: p.package_unit ?? "",
        }))
      );
    } else if (row.no_of_packages || row.package_unit) {
      setPackageLines([{ ...emptyPackageLine(), no_of_packages: row.no_of_packages, package_unit: row.package_unit }]);
    } else {
      setPackageLines([emptyPackageLine()]);
    }

    setShowModal(true);
  }, [initialFormState]);

  /* open image modal */
  const handleImages = useCallback((row) => {
    setImageContainer({
      container_no: row.container_no,
      bl_number: row.bl_number || row.job_no,
    });
    setImageModal(true);
  }, []);

  /* open BL detail modal */
  const handleViewBl = useCallback((row) => {
    setActiveBl(row.bl_no);
    setBlDetailModal(true);
  }, []);

  /* columns for the BL-grouped (first) table */
  const blColumns = useMemo(() => [
    { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
    { header: "BL No.", accessorKey: "bl_no" },
    { header: "Job No", accessorKey: "job_no" },
    { header: "Consignee", accessorKey: "consignees_name" },
    { header: "Containers", accessorKey: "container_count" },
    {
      header: "Images",
      cell: ({ row }) => (
        <span className={`badge ${row.image_count > 0 ? "bg-success" : "bg-secondary"}`}>
          <i className="ri-image-line me-1" />
          {row.image_count}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <button className="btn btn-outline-primary btn-sm" onClick={() => handleViewBl(row)}>
          <i className="ri-eye-line me-1" />
          View
        </button>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ], [handleViewBl]);

  /* columns for the existing flat container table (kept as-is, unchanged) */
  const columns = useMemo(() => [
    { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
    {
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          {/* <i className="ri-edit-line text-primary cursor-pointer" onClick={() => handleEdit(row)} title="Edit" style={{ fontSize: 18 }} /> */}
          <i className="ri-image-2-fill text-success cursor-pointer" onClick={() => handleImages(row)} title="Images" style={{ fontSize: 18 }} />
        </div>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    { header: "BL No.", accessorKey: "bl_no" },
    { header: "Container No.", accessorKey: "container_no" },
    { header: "Seal No.", accessorKey: "seal_no" },
    { header: "Size", accessorKey: "container_size" },
    { header: "Job Ref", accessorKey: "job_id_fk" },
    { header: "Arrival", accessorKey: "arrival_date" },
    { header: "Tare Wt (MT)", accessorKey: "tare_weight" },
    { header: "Gross Wt (MT)", accessorKey: "gross_weight" },
    { header: "No of packages", accessorKey: "no_of_packages" },
    { header: "Package Unit", accessorKey: "package_unit" },
    { header: "Arrival date", accessorKey: "arrival_date" },
    { header: "Shifting Date", accessorKey: "shifting_date" },
    { header: "Container Type", accessorKey: "container_type" },
    { header: "Cargo Description", accessorKey: "cargo_description" },
    { header: "Weight Unit", accessorKey: "weight_unit" },
    { header: "Free Days", accessorKey: "free_days" },
    { header: "Yard Date", accessorKey: "yard_date" },
    { header: "Gross Wt (MT)", accessorKey: "gross_weight" },
    { header: "Storage Date", accessorKey: "storage_date" },
    { header: "Remarks", accessorKey: "remarks" },
    {
      header: "Packages",
      cell: ({ row }) => (
        <span title={summarizePackages(row.packages)}>
          {summarizePackages(row.packages)}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      header: "Images",
      cell: ({ row }) => {
        const count = row.image_count || 0;
        return (
          <span className={`badge ${count > 0 ? "bg-success" : "bg-secondary"}`} style={{ cursor: "pointer" }} onClick={() => handleImages(row)} title="View images">
            <i className="ri-image-line me-1" />
            {count}
          </span>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
    },
  ], [handleImages, handleEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };
    if (name === "job_id_fk") {
      const selected = jobOptions.find((l) => l.value === value);
      if (selected) {
        updated.container_type = selected.container_type || "";
        updated.cargo_description = selected.cargo_desc || "";
      }
    }
    setFormData(updated);
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
    setPackageLines([emptyPackageLine()]);
  };

  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    // At least one complete package line (qty + unit) is required.
    const validLines = packageLines.filter((l) => l.no_of_packages !== "" || l.package_unit !== "");
    const incompleteLine = validLines.find((l) => !l.no_of_packages || !l.package_unit);
    if (validLines.length === 0) {
      showAlert("error", "Oops...", "Add at least one package line (No. of Packages + Package Unit)");
      return;
    }
    if (incompleteLine) {
      showAlert("error", "Oops...", "Every package line needs both a quantity and a unit");
      return;
    }

    try {
      const payload = {
        ...formData,
        packages: validLines.map(({ no_of_packages, package_unit }) => ({ no_of_packages, package_unit })),
      };
      const response = await post("/all_jobs/containers", payload);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  /* keep the BL detail modal's rows in sync whenever containerList refreshes
     (e.g. after an image upload/delete bumps image_count) */
  const activeBlRows = activeBl ? (blGroups[activeBl] || []) : [];

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Containers</div>
          <div className="page-header-sub">
            {blTableData.length} bill{blTableData.length === 1 ? "" : "s"} of lading &nbsp;·&nbsp; {containerList.length} containers at CFS
          </div>
        </div>
        {/* <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          New Container
        </button> */}
      </div>

      {/* first table — grouped by BL number */}
      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Bills of Lading</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Bills of Lading"
            columns={blColumns}
            data={blTableData}
            isGlobalFilter={true}
            isPagination={true}
            isExportButton={false}
          />
        </div>
      </div>

      {/* existing flat container list, kept below for reference / full-detail export */}
      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Containers List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Containers List"
            columns={columns}
            data={containerList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      {/* Container form modal — supports multiple package types per container */}
      <ContainerFormModal
        show={showModal}
        title="Containers"
        formFields={formFields}
        formData={formData}
        packageLines={packageLines}
        onChange={handleChange}
        onPackageLinesChange={setPackageLines}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />

      {/* Image gallery modal */}
      <ContainerImageModal
        show={imageModal}
        container={imageContainer}
        onClose={() => { setImageModal(false); setImageContainer(null); }}
      />

      {/* BL → container detail modal (now a table, one row per container) */}
      <BLContainerDetailModal
        show={blDetailModal}
        blNo={activeBl}
        rows={activeBlRows}
        onClose={() => { setBlDetailModal(false); setActiveBl(null); }}
        onOpenImages={(c) => { setImageContainer(c); setImageModal(true); }}
      />
    </>
  );
};

export default Containers;